import type { OwnerPropertyOut } from '@myrentalmate/api-client';
import { Alert, CircularProgress, Stack, Typography } from '@mui/material';

import { EmailLink, PhoneLink } from '../../components/ContactLinks';
import { DataTable, type DataColumn } from '../../components/DataTable';
import { StatRow, StatTile } from '../../components/Stats';
import { formatMoney, formatMonth, formatOccupancy } from '../../lib/format';
import { OwnerNotLinked } from './OwnerNotLinked';
import { useOwnerHome } from './useOwnerHome';

const COLUMNS: DataColumn<OwnerPropertyOut>[] = [
  { label: 'Property', render: (p) => p.name },
  { label: 'Your Share', align: 'right', render: (p) => `${Number(p.share_percent)}%` },
  { label: 'Units', align: 'right', render: (p) => p.units },
  {
    label: 'Occupancy',
    align: 'right',
    render: (p) => formatOccupancy(p.occupied_units, p.units - p.occupied_units) ?? '–',
  },
];

export function OwnerHomePage() {
  const { home, notLinked, isError } = useOwnerHome();
  if (notLinked) return <OwnerNotLinked />;
  if (isError) return <Alert severity="error">Your portal couldn't be loaded.</Alert>;
  if (!home) return <CircularProgress aria-label="Loading" />;
  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="h5">Welcome, {home.name}</Typography>
        <Typography color="text.secondary">
          Your properties, managed by {home.business_name}.
        </Typography>
      </div>
      <StatRow>
        <StatTile
          label="Balance"
          value={home.balance === null ? '–' : formatMoney(home.balance)}
          hint={
            home.last_statement
              ? `As of your ${formatMonth(home.last_statement)} statement`
              : undefined
          }
        />
        <StatTile label="Collected This Year" value={formatMoney(home.collected_this_year)} />
        <StatTile label="Expenses and Fees" value={formatMoney(home.expenses_this_year)} />
        <StatTile label="Paid to You" value={formatMoney(home.distributed_this_year)} />
      </StatRow>
      {home.balance === null && (
        <Alert severity="info">
          Your figures appear here when {home.business_name} publishes your first statement.
        </Alert>
      )}
      <Typography variant="h6">Your Properties</Typography>
      <DataTable columns={COLUMNS} rows={home.properties} rowKey={(p) => p.id} />
      {(home.business_phone || home.business_email) && (
        <Stack spacing={0.5}>
          <Typography variant="h6">Questions?</Typography>
          <Typography component="div">
            {home.business_phone && <PhoneLink phone={home.business_phone} />}
          </Typography>
          <Typography component="div">
            {home.business_email && <EmailLink email={home.business_email} />}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
